import { Controller } from "stimulus"
import { showTarget, hideTarget } from '../../helpers/visibility'

export default class extends Controller {
  static targets = [
    "actionDropdown",
    "bulkEditButton", 
    "checkbox",
    "extraIds",
    "extraRow",
    "selectAllCheckbox",
    "groupCheckbox",
    "pricingWrapper",
    "flatRateFields",
    "hourlyRateFields", 
    "dailyRateFields",
    "packageSelectorWrapper",
    "advancedOptionsWrapper",
    "leadTimeFields",
    "bookingExtensionFields",
    "moveToGroupWrapper"
  ]

  connect() {
    this.updateCheckboxes()
    if(this.hasActionDropdownTarget) {
      this.refreshActionDropdown()
    }
  }

  refreshActionDropdown() {
    if(!this.hasActionDropdownTarget) return
    
    const action = this.actionDropdownTarget.value
    this.hideAllWrappers()
    switch(action) {
      case "update_pricing":
        if(this.hasPricingWrapperTarget) showTarget(this.pricingWrapperTarget)
        // Show flat rate fields by default
        this.updatePriceFields()
        break;
      case "set_associated_packages":
        if(this.hasPackageSelectorWrapperTarget) showTarget(this.packageSelectorWrapperTarget)
        break;
      case "update_advanced_options":
        if(this.hasAdvancedOptionsWrapperTarget) {
          showTarget(this.advancedOptionsWrapperTarget)
        }
        break;
      case "move_to_group":
        if(this.hasMoveToGroupWrapperTarget) {
          showTarget(this.moveToGroupWrapperTarget)
        }
        break;
      case "archive_extras":
      case "unarchive_extras":
      case "delete_extras":
        // No additional fields needed
        break;
      default:
        console.warn(`Unhandled action: ${action}`)
    }
  }

  updatePriceFields() {
    // Find the selected pricing method from radio buttons
    const priceMethodRadio = this.element.querySelector('input[name="command[price_method]"]:checked')
    const pricingMethod = priceMethodRadio ? priceMethodRadio.value : ''
    
    this.hideAllPriceFields()

    switch(pricingMethod) {
      case "flat_rate":
        if(this.hasFlatRateFieldsTarget) {
          showTarget(this.flatRateFieldsTarget)
        }
        break;
      case "hourly":
        if(this.hasHourlyRateFieldsTarget) {
          showTarget(this.hourlyRateFieldsTarget)
        }
        break;
      case "daily":
        if(this.hasDailyRateFieldsTarget) {
          showTarget(this.dailyRateFieldsTarget)
        }
        break;
      case "":
      default:
        // "Leave unchanged" selected or no selection - hide all price fields
        break;
    }
  }



  updateCheckboxes() {
    const checked = this.checkboxTargets.filter(checkbox => checkbox.checked)
    const unchecked = this.checkboxTargets.filter(checkbox => !checkbox.checked)

    if(checked.length > 0) {
      showTarget(this.bulkEditButtonTarget)
    }
    else {
      hideTarget(this.bulkEditButtonTarget)
    }

    if(this.hasExtraIdsTarget) {
      this.extraIdsTarget.value = checked.map(checkbox => checkbox.value).join(",")
    }
    
    // Update select all checkbox state
    if(this.hasSelectAllCheckboxTarget) {
      if(checked.length === this.checkboxTargets.length && checked.length > 0) {
        this.selectAllCheckboxTarget.checked = true
        this.selectAllCheckboxTarget.indeterminate = false
      } else if(checked.length > 0) {
        this.selectAllCheckboxTarget.checked = false
        this.selectAllCheckboxTarget.indeterminate = true
      } else {
        this.selectAllCheckboxTarget.checked = false
        this.selectAllCheckboxTarget.indeterminate = false
      }
    }
    
    // Update group checkbox states
    this.updateGroupCheckboxStates()
  }

  toggleSelectAll() {
    if(this.selectAllCheckboxTarget.checked) {
      this.checkboxTargets.forEach(checkbox => {
        checkbox.checked = true
      })
    }
    else {
      this.checkboxTargets.forEach(checkbox => {
        checkbox.checked = false
      })
    }
    this.updateCheckboxes()
  }

  getSelectedExtras() {
    return this.checkboxTargets.filter(checkbox => checkbox.checked)
  }


  hideAllWrappers() {
    if(this.hasPricingWrapperTarget) hideTarget(this.pricingWrapperTarget)
    if(this.hasPackageSelectorWrapperTarget) hideTarget(this.packageSelectorWrapperTarget)
    if(this.hasAdvancedOptionsWrapperTarget) hideTarget(this.advancedOptionsWrapperTarget)
    if(this.hasMoveToGroupWrapperTarget) hideTarget(this.moveToGroupWrapperTarget)
  }

  hideAllPriceFields() {
    if(this.hasFlatRateFieldsTarget) hideTarget(this.flatRateFieldsTarget)
    if(this.hasHourlyRateFieldsTarget) hideTarget(this.hourlyRateFieldsTarget)
    if(this.hasDailyRateFieldsTarget) hideTarget(this.dailyRateFieldsTarget)
  }

  updateAdvancedFields() {
    // Show/hide lead time field based on requires lead time selection
    const leadTimeRadio = this.element.querySelector('input[name="command[requires_lead_time]"]:checked')
    if (leadTimeRadio && leadTimeRadio.value === "yes") {
      if(this.hasLeadTimeFieldsTarget) showTarget(this.leadTimeFieldsTarget)
    } else {
      if(this.hasLeadTimeFieldsTarget) hideTarget(this.leadTimeFieldsTarget)
    }
    
    // Show/hide booking extension field based on adds minutes selection
    const bookingExtensionRadio = this.element.querySelector('input[name="command[adds_minutes_to_event_length]"]:checked')
    if (bookingExtensionRadio && bookingExtensionRadio.value === "yes") {
      if(this.hasBookingExtensionFieldsTarget) showTarget(this.bookingExtensionFieldsTarget)
    } else {
      if(this.hasBookingExtensionFieldsTarget) hideTarget(this.bookingExtensionFieldsTarget)
    }
  }

  toggleGroupSelection(event) {
    const groupCheckbox = event.target
    const groupId = groupCheckbox.dataset.groupId
    const isChecked = groupCheckbox.checked
    
    // Find all checkboxes for extras in this group
    const extraRowsInGroup = this.getExtrasInGroup(groupId)
    
    // Update all checkboxes in this group
    extraRowsInGroup.forEach(row => {
      const checkbox = row.querySelector('input[data-extra-bulk-editor-target="checkbox"]')
      if (checkbox) {
        checkbox.checked = isChecked
      }
    })
    
    // Update the overall state
    this.updateCheckboxes()
    this.updateGroupCheckboxStates()
  }
  
  getExtrasInGroup(groupId) {
    // Find the group header row
    const groupCheckbox = this.groupCheckboxTargets.find(cb => cb.dataset.groupId === groupId)
    if (!groupCheckbox) return []
    
    const groupRow = groupCheckbox.closest('tr')
    const tbody = groupRow.parentElement
    
    // Get all rows after the group header until the next group header or end
    const extraRows = []
    let currentRow = groupRow.nextElementSibling
    
    while (currentRow && !currentRow.classList.contains('no-drag')) {
      extraRows.push(currentRow)
      currentRow = currentRow.nextElementSibling
    }
    
    return extraRows
  }
  
  updateGroupCheckboxStates() {
    this.groupCheckboxTargets.forEach(groupCheckbox => {
      const groupId = groupCheckbox.dataset.groupId
      const extrasInGroup = this.getExtrasInGroup(groupId)
      const checkboxesInGroup = extrasInGroup.map(row => 
        row.querySelector('input[data-extra-bulk-editor-target="checkbox"]')
      ).filter(Boolean)
      
      const checkedCount = checkboxesInGroup.filter(cb => cb.checked).length
      
      if (checkedCount === 0) {
        groupCheckbox.checked = false
        groupCheckbox.indeterminate = false
      } else if (checkedCount === checkboxesInGroup.length) {
        groupCheckbox.checked = true
        groupCheckbox.indeterminate = false
      } else {
        groupCheckbox.checked = false
        groupCheckbox.indeterminate = true
      }
    })
  }
}
